import { format } from 'date-fns';
import { get } from 'lodash-es';

import DraftsIcon from '@mui/icons-material/Drafts';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ReplyIcon from '@mui/icons-material/Reply';
import SendIcon from '@mui/icons-material/Send';
import { Box, Chip, Tooltip, Typography } from '@mui/material';

/**
 * Email metrics interface matching backend response
 */
interface EmailMetrics {
  status: 'pending' | 'sent' | 'opened' | 'clicked' | 'replied' | 'bounced';
  sent_at: string | null;
  opened_at: string | null;
  clicked_at: string | null;
  replied_at: string | null;
}

interface LeadWithEmailMetrics {
  email_metrics?: EmailMetrics | null;
}

/**
 * Status configuration for email statuses
 */
const emailStatusConfig: Record<string, { color: string; bgColor: string; label: string; icon: JSX.Element }> = {
  pending: {
    color: '#9e9e9e',
    bgColor: 'rgba(158, 158, 158, 0.15)',
    label: 'Pending',
    icon: <MailOutlineIcon sx={{ fontSize: 14 }} />,
  },
  sent: {
    color: '#2196f3',
    bgColor: 'rgba(33, 150, 243, 0.15)',
    label: 'Sent',
    icon: <SendIcon sx={{ fontSize: 14 }} />,
  },
  opened: {
    color: '#4caf50',
    bgColor: 'rgba(76, 175, 80, 0.15)',
    label: 'Opened',
    icon: <DraftsIcon sx={{ fontSize: 14 }} />,
  },
  clicked: {
    color: '#9c27b0',
    bgColor: 'rgba(156, 39, 176, 0.15)',
    label: 'Clicked',
    icon: <DraftsIcon sx={{ fontSize: 14 }} />,
  },
  replied: {
    color: '#00bcd4',
    bgColor: 'rgba(0, 188, 212, 0.15)',
    label: 'Replied',
    icon: <ReplyIcon sx={{ fontSize: 14 }} />,
  },
  bounced: {
    color: '#f44336',
    bgColor: 'rgba(244, 67, 54, 0.15)',
    label: 'Bounced',
    icon: <ErrorOutlineIcon sx={{ fontSize: 14 }} />,
  },
};

/**
 * Format a date string for display
 */
const formatDate = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '-';
  try {
    return format(new Date(dateStr), 'MMM d');
  } catch {
    return '-';
  }
};

/**
 * Format a date string with time for tooltips
 */
const formatDateTime = (dateStr: string | null | undefined): string => {
  if (!dateStr) return 'Not yet';
  try {
    return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
  } catch {
    return 'Invalid date';
  }
};

/**
 * Create email status columns for the leads table
 * These columns show email campaign metrics for each lead
 */
export const createEmailStatusColumns = () => [
  {
    field: 'email_status',
    headerName: 'Email Status',
    headerClassName: 'super-bold--header',
    width: 130,
    minWidth: 110,
    hideable: true,
    sortable: true,
    resizable: true,
    renderCell: ({ row }: { row: LeadWithEmailMetrics }) => {
      const metrics = get(row, 'email_metrics');
      if (!metrics) {
        return (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        );
      }

      const status = metrics.status || 'pending';
      const config = emailStatusConfig[status] || emailStatusConfig.pending;

      return (
        <Tooltip title={`Status: ${config.label}`}>
          <Chip
            icon={config.icon}
            label={config.label}
            size="small"
            sx={{
              backgroundColor: config.bgColor,
              color: config.color,
              fontWeight: 500,
              fontSize: '0.75rem',
              '& .MuiChip-icon': {
                color: config.color,
              },
            }}
          />
        </Tooltip>
      );
    },
  },
  {
    field: 'email_sent_at',
    headerName: 'Sent',
    headerClassName: 'super-bold--header',
    width: 90,
    minWidth: 80,
    hideable: true,
    sortable: true,
    resizable: true,
    renderCell: ({ row }: { row: LeadWithEmailMetrics }) => {
      const metrics = get(row, 'email_metrics');
      const sentAt = metrics?.sent_at;

      if (!sentAt) {
        return (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        );
      }

      return (
        <Tooltip title={`Sent: ${formatDateTime(sentAt)}`}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <SendIcon sx={{ color: '#2196f3', fontSize: 14 }} />
            <Typography variant="body2" color="text.secondary">
              {formatDate(sentAt)}
            </Typography>
          </Box>
        </Tooltip>
      );
    },
  },
  {
    field: 'email_opened_at',
    headerName: 'Opened',
    headerClassName: 'super-bold--header',
    width: 90,
    minWidth: 80,
    hideable: true,
    sortable: true,
    resizable: true,
    renderCell: ({ row }: { row: LeadWithEmailMetrics }) => {
      const metrics = get(row, 'email_metrics');
      const openedAt = metrics?.opened_at;

      if (!openedAt) {
        return (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        );
      }

      return (
        <Tooltip title={`Opened: ${formatDateTime(openedAt)}`}>
          <Box display="flex" alignItems="center" gap={0.5}>
            <DraftsIcon sx={{ color: '#4caf50', fontSize: 14 }} />
            <Typography variant="body2" color="#4caf50">
              {formatDate(openedAt)}
            </Typography>
          </Box>
        </Tooltip>
      );
    },
  },
  {
    field: 'email_replied_at',
    headerName: 'Replied',
    headerClassName: 'super-bold--header',
    width: 100,
    minWidth: 90,
    hideable: true,
    sortable: true,
    resizable: true,
    renderCell: ({ row }: { row: LeadWithEmailMetrics }) => {
      const metrics = get(row, 'email_metrics');
      const repliedAt = metrics?.replied_at;

      if (!repliedAt) {
        return (
          <Typography variant="body2" color="text.secondary">
            -
          </Typography>
        );
      }

      return (
        <Tooltip title={`Replied: ${formatDateTime(repliedAt)}`}>
          <Chip
            icon={<ReplyIcon sx={{ fontSize: 14 }} />}
            label={formatDate(repliedAt)}
            size="small"
            sx={{
              backgroundColor: 'rgba(0, 188, 212, 0.15)',
              color: '#00bcd4',
              fontWeight: 600,
              fontSize: '0.75rem',
              '& .MuiChip-icon': {
                color: '#00bcd4',
              },
            }}
          />
        </Tooltip>
      );
    },
  },
];

export default createEmailStatusColumns;
